import Cookies from "js-cookie";
import { get, includes, toNumber } from "lodash";
import { useCallback, useMemo } from "react";
import {
  useAccountQuery,
  useAccountLazyQuery,
  Account,
} from "@/features/workspace/graphql/generated";
import { useAccountSwitch } from "./useAccountSwitch";
import { useLazyAccountMembers } from "./useAccountMembers";
import {
  AssessmentSurveyConfig,
  AssessmentSurveyType,
} from "@/shared/constants/assessmentSurveys";
import { getOrderedDimensionConfig } from "@/shared/utils/selectors/getDimensionTypes";

interface AccountConfig {
  [key: string]: string;
}

export const useLazyAccount = () => {
  const [fetchAccount, { data, loading }] = useAccountLazyQuery();

  const handleFetchAccount = useCallback(async (accountId: number) => {
    const account = await fetchAccount({ variables: { accountId } });
    return account;
  }, []);

  const account = data?.account || ({} as Account);

  return {
    handleFetchAccount,
    account,
    loading,
  };
};

export const useLoadInitialAccount = () => {
  const app_default_account = toNumber(Cookies.get("account_id"));

  const { handleSwitchAccount } = useAccountSwitch();

  const [loadAccount, { data, loading }] = useAccountLazyQuery({
    onCompleted: (data: any) => {
      if (data.account?.id && app_default_account !== data.account?.id) {
        handleSwitchAccount(data.account?.id);
      }
    },
  });

  const { fetchAccountMembers } = useLazyAccountMembers();

  const handleLoadInitialAccount = useCallback(async (accountId: number) => {
    const account = await loadAccount({ variables: { accountId } });
    await fetchAccountMembers();
    return account;
  }, []);

  const account = data?.account || ({} as Account);

  return {
    handleLoadInitialAccount,
    account,
    loading,
  };
};

export const useAccount = (accountId?: number) => {
  const app_default_account =
    toNumber(Cookies.get("account_id")) ||
    toNumber(localStorage.getItem("account_id"));

  const account_id = accountId || app_default_account;

  const { data, loading } = useAccountQuery({
    variables: {
      accountId: account_id!,
    },
    skip: !account_id,
    fetchPolicy: "cache-first",
    nextFetchPolicy: "cache-first",
  });

  const account = useMemo(() => data?.account || ({} as Account), [data]);

  const getAccountConfigValue = useCallback(
    (key: string) => {
      return get(account?.config, key);
    },
    [account]
  );

  const getAccountAssessmentSurveys = useCallback(
    (only?: AssessmentSurveyType[]) => {
      const allSurveys = getAccountConfigValue(
        "assessment_surveys"
      ) as AssessmentSurveyConfig[];
      if (!only || only.length === 0) {
        return getOrderedDimensionConfig(allSurveys);
      }
      return getOrderedDimensionConfig(
        allSurveys.filter((survey) => includes(only, survey.type))
      );
    },
    [account]
  );

  return {
    account,
    loading,
    getAccountConfigValue,
    getAccountAssessmentSurveys,
  };
};
